import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { Analytics } from '../types/business';
import { handleError } from '../utils/businessValidation';

export const useAnalytics = (businessId?: string) => {
  const [analytics, setAnalytics] = useState<Analytics>({
    totalInventory: 0,
    lowStockItems: 0,
    activeDeals: 0,
    pendingBookings: 0,
    monthlyBookings: 0,
    completedBookings: 0,
    revenue: 0
  });
  const [loading, setLoading] = useState(false);

  const fetchAnalytics = async () => {
    if (!businessId) return;

    try {
      setLoading(true);

      // Fetch inventory data
      const { data: inventoryData, error: inventoryError } = await supabase
        .from("tyre_inventory")
        .select("quantity, price")
        .eq("business_id", businessId);

      if (inventoryError) {
        console.error('Inventory fetch error:', inventoryError);
      }

      // Fetch deals data
      const { data: dealsData, error: dealsError } = await supabase
        .from("deals")
        .select("*")
        .eq("business_id", businessId)
        .eq("active", true);

      if (dealsError) {
        console.error('Deals fetch error:', dealsError);
      }

      // Fetch bookings data
      const { data: bookingsData, error: bookingsError } = await supabase
        .from("bookings")
        .select("*")
        .eq("business_id", businessId);

      if (bookingsError) {
        console.error('Bookings fetch error:', bookingsError);
      }

      // Calculate analytics
      const totalInventory = inventoryData?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
      const lowStockItems = inventoryData?.filter(item => (item.quantity || 0) < 10).length || 0;
      const activeDeals = dealsData?.length || 0;
      
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();

      const pendingBookings = bookingsData?.filter(booking => booking.status === 'pending').length || 0;
      const completedBookings = bookingsData?.filter(booking => booking.status === 'completed').length || 0;
      const monthlyBookings = bookingsData?.filter(booking => {
        const bookingDate = new Date(booking.created_at);
        return bookingDate.getMonth() === thisMonth && bookingDate.getFullYear() === thisYear;
      }).length || 0;

      // Calculate estimated revenue (simplified calculation)
      const revenue = inventoryData?.reduce((sum, item) => {
        const soldQuantity = Math.max(0, 100 - (item.quantity || 0)); // Assume starting stock was 100
        return sum + (soldQuantity * (item.price || 0));
      }, 0) || 0;

      setAnalytics({
        totalInventory,
        lowStockItems,
        activeDeals,
        pendingBookings,
        monthlyBookings,
        completedBookings,
        revenue
      });

    } catch (error) {
      console.error('Analytics fetch error:', handleError(error, 'Failed to fetch analytics'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchAnalytics();
    }
  }, [businessId]);

  return {
    analytics,
    loading,
    refetch: fetchAnalytics
  };
};